import { Component } from '@theme/component';

/**
 * The always-visible header search field (snippets/header-search-field.liquid).
 *
 * Wraps the theme's predictive-search-component, which owns fetching and rendering results. This
 * component only decides when the results dropdown may open, and filters keyboard handling: the
 * predictive search was built for the modal and captures Tab, which would trap keyboard users in a
 * field that is always on the page.
 *
 * @typedef {object} Refs
 * @property {HTMLElement & { onSearchKeyDown: (event: KeyboardEvent) => void }} predictiveSearch - The wrapped predictive search component.
 *
 * @extends {Component<Refs>}
 */
class HeaderSearchComponent extends Component {
  requiredRefs = ['predictiveSearch'];

  /** @type {AbortController | null} */
  #controller = null;

  /** Listens for focus and outside presses that open and close the dropdown; a fresh controller per connect keeps reconnects working. */
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('focusin', this.#open, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown, { signal });
    // A page restored from the back/forward cache would otherwise come back with the dropdown open.
    window.addEventListener('pageshow', this.#close, { signal });
  }

  /** Removes the listeners added in connectedCallback. */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Routes keys from the field and its results to the predictive search. Tab keeps the native focus
   * order, Escape clears the query from anywhere in the field, and the arrow and Enter keys only drive
   * the results while results are showing (otherwise Enter submits the form natively).
   * @param {KeyboardEvent} event - The keyboard event; its target is the input or the form.
   */
  onSearchKeyDown = (event) => {
    const { predictiveSearch } = this.refs;
    const searchInput = this.querySelector('input[type="search"]');
    const isFromSearchInput = event.target === searchInput;

    if (event.key === 'Tab' || !(searchInput instanceof HTMLInputElement)) return;

    if (event.key === 'Escape') {
      if (!searchInput.value) return;
      if (!isFromSearchInput) searchInput.focus();
      predictiveSearch.onSearchKeyDown(event);
      return;
    }

    if (isFromSearchInput && this.querySelector('[data-search-results]')) {
      predictiveSearch.onSearchKeyDown(event);
    }
  };

  /** Marks the field as in use; CSS shows the dropdown once there is also a query with results. */
  #open = () => {
    this.toggleAttribute('data-open', true);
  };

  /** Marks the field as no longer in use, which hides the dropdown while keeping the query and results. */
  #close = () => {
    this.removeAttribute('data-open');
  };

  /**
   * Closes when focus moves to an element outside the field. A null relatedTarget is left to the
   * pointerdown handler: Safari does not focus clicked links, so clicking a result blurs the input
   * without focusing anything, and closing then would hide the result before its click lands.
   * @param {FocusEvent} event - The focusout event.
   */
  #handleFocusOut = (event) => {
    const { relatedTarget } = event;
    if (relatedTarget instanceof Node && !this.contains(relatedTarget)) this.#close();
  };

  /**
   * Closes when the visitor presses anywhere outside the field.
   * @param {PointerEvent} event - The pointerdown event.
   */
  #handleDocumentPointerDown = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#close();
  };
}

if (!customElements.get('header-search-component')) {
  customElements.define('header-search-component', HeaderSearchComponent);
}
